import React, { useEffect } from 'react'
import styled from 'styled-components'
import { colors } from '~/design'
import Select from 'react-select'
import {
  Insight as InsightType,
  InsightAction,
  VariablePreview,
} from '~/packages/general/variable/variables.typing'
import { getAlertColor, getAlertLabel, getAlertTextColor } from '~/packages/general/variable/spaceTime.data'
import Visualization from './visualization/visualization.component'
import { useMedia } from 'react-use'
import { AnyVisualization, ButtonsPanelButton } from './visualization/visualization.types'

type VariablePreviewAction = Extract<InsightAction, { type: 'variablePreview' }>

type WrapperProps = {
  $isExpanded: boolean
  $isDisabled: boolean
  $isButton: boolean
  $isClickable?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${({ $isButton }) => ($isButton ? '8px' : '0')};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  text-align: left;
  width: 100%;
  min-width: 0;
  box-shadow: ${({ $isButton }) => ($isButton ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none')};
  background: #fff;
  color: #363636;
  position: relative;
  transform: scale(1);
  transform-origin: center;
  filter: ${({ $isDisabled }) => ($isDisabled ? 'grayscale(25%) brightness(0.95)' : 'none')};
  transition: filter 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ $isDisabled }) =>
      $isDisabled
        ? 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(240,240,240,0.8) 100%)'
        : 'transparent'};
    border-radius: ${({ $isButton }) => ($isButton ? '8px' : '0')};
    z-index: 1;
    opacity: ${({ $isDisabled }) => ($isDisabled ? 1 : 0)};
    visibility: ${({ $isDisabled }) => ($isDisabled ? 'visible' : 'hidden')};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &:hover {
    box-shadow: ${({ $isDisabled, $isButton }) =>
      !$isButton
        ? 'none'
        : $isDisabled
          ? '0 2px 8px rgba(0, 0, 0, 0.1)'
          : '0 4px 16px rgba(15, 67, 138, 0.15)'};
  }

  &:active {
    transform: ${({ $isClickable }) => ($isClickable ? 'scale(1.02)' : 'none')};
  }
`

type TopSectionProps = {
  $isPreviewMobile: boolean
  $isExpanded: boolean
}

const TopSection = styled.div<TopSectionProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 16px 0px 16px;
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: ${({ $isPreviewMobile }) =>
      $isPreviewMobile ? '16px 16px 16px 16px' : '16px 16px 0px 16px'};
  }
`
const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 16px 16px 16px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: bold;
  color: #363636;
  flex: 1;
`

const Alert = styled.small<{ alert: NonNullable<InsightType['alert']> }>`
  padding: 0.3rem 1rem;
  border-radius: 8px;
  align-self: flex-start;
  text-transform: uppercase;
  font-weight: bold;
  text-align: center;
  background-color: ${({ alert }) => getAlertColor(alert)};
  color: ${({ alert }) => getAlertTextColor(alert)};
`

const Description = styled.small`
  margin: 0;
  font-size: 14px;
  color: #363636;
  line-height: 1.4;
  margin-bottom: 8px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;

  select {
    width: 100%;
    min-height: 32px;
    font-size: 0.9rem;
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    color: #111827;
    background: #ffffff;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }
`

const VisualizationsContainer = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;

  &.list {
    padding: 0 14px;
  }
`

interface InsightProps extends React.HTMLAttributes<HTMLDivElement> {
  insight: InsightType
  isExpanded: boolean
  isDisabled: boolean
  onSelect?: () => void
  variablePreview?: VariablePreview | null
  onVariablePreviewChange?: (variablePreview: VariablePreview) => void
  activeButton?: string
  onActionButtonClick?: (button: ButtonsPanelButton) => void
  isList?: boolean
  isButton?: boolean
  isClickable?: boolean
}

export default function Insight({
  insight,
  isExpanded,
  isDisabled,
  onSelect,
  variablePreview,
  onVariablePreviewChange,
  activeButton: activeButtonProp,
  onActionButtonClick,
  isList = false,
  isButton = true,
  isClickable = true,
  ...props
}: InsightProps) {
  const isMobile = useMedia('(max-width: 768px)')
  const insightActions = insight.actions ?? []
  const insightVisualizations = insight.visualizations ?? []
  const [activeButton, setActiveButton] = React.useState<string | undefined>(
    insightActions.find(action => action.type === 'variablePreview')?.label,
  )
  const [selectedVariableAction, setSelectedVariableAction] =
    React.useState<VariablePreviewAction | null>(null)

  const onlyInformationsType = [
    'performanceBelowExpectations',
    'lowTerritorialCoverage',
    'sparseDataDetection',
    'exportItems',
  ] as const

  // Evitar o comportamento padrão dos cards com clicks
  const onlyInformations = React.useMemo(() => {
    const matchesType =
      !!insight.type &&
      onlyInformationsType.includes(insight.type as (typeof onlyInformationsType)[number])

    const matchesLegacy =
      !insight.type &&
      insightVisualizations.every(visualization => visualization.renderer === 'buttons')

    return matchesType || matchesLegacy
  }, [insight.type, insightVisualizations])

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()

    const target = event.target as Element
    // Plotly modebar controls
    const isInteractiveElement = !!target.closest(
      'button, select, a, [role="button"], [role="switch"], [role="combobox"]',
    )
    const isPlotlyToolbar = !!target.closest(
      '.modebar, .modebar-container, .modebar-group, .modebar-btn',
    )
    const isPlotlyChart = !!target.closest('.js-plotly-plot')
    // React-select control/menu
    const isReactSelect = !!target.closest(
      '.insight-select, [class^="insight-select"], [class*=" insight-select"]',
    )
    if (isInteractiveElement || isPlotlyToolbar || isPlotlyChart || isReactSelect) return

    if (onlyInformations) return

    onSelect?.()
  }

  const variableActions = insightActions.filter(
    (action): action is VariablePreviewAction => action.type === 'variablePreview',
  )

  const defaultVariableActionLabel = React.useMemo(
    () => (variableActions.length > 0 ? variableActions[0].label : undefined),
    [insight.key],
  )

  const handleActionButtonClick = (button: ButtonsPanelButton) => {
    onActionButtonClick?.(button)
    setActiveButton(button.label)
  }

  useEffect(() => {
    if (!isExpanded) {
      setSelectedVariableAction(null)
      setActiveButton(undefined)
    }
  }, [isExpanded])

  useEffect(() => {
    if (isExpanded) {
      setSelectedVariableAction(null)
      setActiveButton(defaultVariableActionLabel)
    }
  }, [isExpanded, defaultVariableActionLabel])

  const isPreviewMobile = isMobile && !isExpanded

  return (
    <Wrapper
      $isExpanded={isExpanded}
      $isDisabled={isDisabled}
      $isButton={isButton}
      $isClickable={isClickable}
      onClick={isButton || isClickable ? handleClick : undefined}
      {...props}
    >
      <TopSection
        $isPreviewMobile={isPreviewMobile}
        $isExpanded={isExpanded}
      >
        <Header>
          <Title>{insight.title}</Title>
          {insight.alert ? (
            <Alert alert={insight.alert}>{getAlertLabel(insight.alert)}</Alert>
          ) : null}
        </Header>
        <Description>{insight.description}</Description>
      </TopSection>

      <VisualizationsContainer className={isList ? 'list' : ''}>
        {insightVisualizations.map((visualization, index) => (
          <Visualization
            key={index}
            visualization={visualization as AnyVisualization}
            isExpanded={isExpanded}
            activeButton={activeButtonProp ?? activeButton}
            onButtonClick={handleActionButtonClick}
            activeLabel={selectedVariableAction?.label}
            alert={insight.alert}
            insightTitle={insight.title}
          />
        ))}
      </VisualizationsContainer>

      {isExpanded && variableActions.length > 0 ? (
        <BottomSection>
          <Actions>
            {variableActions.length > 0 ? (
              <Select
                className='insight-select'
                classNamePrefix='insight-select'
                value={(() => {
                  const label = selectedVariableAction?.label ?? defaultVariableActionLabel
                  return label ? { value: label, label } : null
                })()}
                onChange={opt => {
                  const label = (opt as { value: string } | null)?.value
                  if (!label) return
                  const selectedAction = variableActions.find(a => a.label === label)
                  if (!selectedAction) return
                  onVariablePreviewChange?.(selectedAction)
                  setSelectedVariableAction(selectedAction)
                }}
                options={variableActions.map(a => ({ value: a.label, label: a.label }))}
                isClearable={false}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuShouldScrollIntoView={false}
                styles={{
                  container: base => ({
                    ...base,
                    width: '100%',
                  }),
                  control: base => ({
                    ...base,
                    minHeight: 32,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    borderColor: '#d1d5db',
                    boxShadow: 'none',
                    '&:hover': { borderColor: 'hsl(0,0%,70%)' },
                  }),
                  valueContainer: base => ({
                    ...base,
                    padding: '0 6px',
                  }),
                  option: (styles, { isFocused, isSelected }) => ({
                    ...styles,
                    fontSize: '0.9rem',
                    backgroundColor: isSelected
                      ? colors.headerBlue
                      : isFocused
                        ? colors.lightBlue
                        : undefined,
                    color: isSelected ? '#fff' : isFocused ? 'rgba(0,0,0,0.85)' : styles.color,
                  }),
                  menuPortal: base => ({ ...base, zIndex: 10060 }),
                }}
                noOptionsMessage={() => 'Sem resultados'}
              />
            ) : null}
          </Actions>
        </BottomSection>
      ) : null}
    </Wrapper>
  )
}
